// WebMenu API types — matches the backend response shape (C# Product model)

export type ValuePerLang = {
  lang: string;
  value: string;
};

export type ProductFeature = {
  _id: string;
  blendModifierId?: string;
  FoodicsModifierId?: string;
  blendGroupModifierId?: string;
  FoodicsGroupModifierId?: string;
  name?: string;
  nameAr?: string;
  nameLang?: ValuePerLang[];
  label?: string;
  value?: string;
  price?: number;
  priceAdd?: number;
  priceCurrency?: string;
};

export type TagItem = {
  key: string;
  name: ValuePerLang[];
};

export type WebMenuCategory = {
  id: string;
  name: string;
  name_en: string;
  nameLocalized: string;
  icon: string;
  products: WebMenuProduct[];
};

export type WebMenuProduct = {
  _id: string;
  blendId?: number;
  foodicsId?: string;
  projectId?: string;
  chatBotId?: string;
  menuName?: string;
  menuNameLang?: ValuePerLang[];
  price: number;
  nameLang: ValuePerLang[];
  descriptionLang: ValuePerLang[];
  imageUrl: string;
  itemLink: string;
  ignoreSearch?: boolean;
  isActive: boolean;
  isOffer: boolean;
  hasSize: boolean;
  sizes: ProductFeature[];
  hasBeverage: boolean;
  minBeverageCount: number;
  maxBeverageCount: number;
  beverage: ProductFeature[];
  hasTaste: boolean;
  taste: ProductFeature[];
  hasSide: boolean;
  minSidesCount: number;
  maxSidesCount: number;
  sides: ProductFeature[];
  hasAddon: boolean;
  minAddonsCount: number;
  maxAddonsCount: number;
  addons: ProductFeature[];
  hasBreadsKind: boolean;
  breadsKind: ProductFeature[];
  hasSaladType: boolean;
  saladType: ProductFeature[];
  hasAttachedOffer: boolean;
  minAttachedOfferCount: number;
  maxAttachedOfferCount: number;
  attachedOffer: ProductFeature[];
  hasRiceKind: boolean;
  riceKind: ProductFeature[];
  startAt: string | null;
  endAt: string | null;
  deleted: boolean;
  isQuantityLimit: boolean;
  quantity: number;
  calories: number;
  bestSeller: boolean;
  ordersCount: number;
  totalRevenu: number;
  productType: string;
  productSource: string;
  bookDetail: Record<string, unknown>;
  categoryId: string;
  allergies: { name: string; nameLocalized?: string }[];
  servingUnit: string;
  servingSize: number;
  tags: TagItem[];
  isScheduled: boolean;
  scheduledDaysCount: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  isAvailable: boolean;
};

export type WebMenu = {
  _id: string;
  categories: WebMenuCategory[];
  tag_items: TagItem[];
};

// ---- Backward-compatible aliases used by existing UI components ----

export type MenuItem = {
  id: string;
  name: string;
  nameAr: string;
  menuName: string;
  menuNameAr: string;
  description: string;
  descriptionAr: string;
  price: number;
  image: string;
  category: string;
  tags: TagItem[];
  popular?: boolean;
  ignoreSearch?: boolean;
  blendId?: number;
  foodicsId?: string;
  projectId?: string;
  chatBotId?: string;
  itemLink?: string;
  hasSize: boolean;
  sizes: ProductFeature[];
  hasBeverage: boolean;
  minBeverageCount: number;
  maxBeverageCount: number;
  beverage: ProductFeature[];
  hasTaste: boolean;
  taste: ProductFeature[];
  hasSide: boolean;
  minSidesCount: number;
  maxSidesCount: number;
  sides: ProductFeature[];
  hasAddon: boolean;
  minAddonsCount: number;
  maxAddonsCount: number;
  addons: ProductFeature[];
  hasBreadsKind: boolean;
  breadsKind: ProductFeature[];
  hasSaladType: boolean;
  saladType: ProductFeature[];
  hasAttachedOffer: boolean;
  minAttachedOfferCount: number;
  maxAttachedOfferCount: number;
  attachedOffer: ProductFeature[];
  hasRiceKind: boolean;
  riceKind: ProductFeature[];
  removable?: string[];
};

export type Category = {
  id: string;
  name: string;
  nameAr: string;
  icon: string;
};

/** Helper to get a value from ValuePerLang array by language code */
export function getLangValue(langs: ValuePerLang[] | undefined, lang: string, fallback = ""): string {
  if (!langs || langs.length === 0) return fallback;
  const match = langs.find((l) => l.lang === lang);
  return match?.value || langs[0]?.value || fallback;
}

export function getTagLabel(tag: TagItem, lang: string, fallback = ""): string {
  return getLangValue(tag.name, lang, fallback || tag.key);
}

export function normalizeTagItem(tag: string | TagItem): TagItem {
  if (typeof tag !== "string") return tag;

  const label = tag
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

  return {
    key: tag,
    name: [
      { lang: "en", value: label },
      { lang: "ar", value: label },
    ],
  };
}

/** Convert a WebMenuProduct to the legacy MenuItem shape used by existing components */
export function productToMenuItem(product: WebMenuProduct, categoryId: string): MenuItem {
  const rawNameEn = getLangValue(product.nameLang, "en", product.menuName || "");
  const rawNameAr = getLangValue(product.nameLang, "ar", rawNameEn);
  const menuNameEn = getLangValue(product.menuNameLang, "en", product.menuName || rawNameEn);
  const menuNameAr = getLangValue(product.menuNameLang, "ar", rawNameAr || menuNameEn || rawNameEn);
  const descEn = getLangValue(product.descriptionLang, "en", "");
  const descAr = getLangValue(product.descriptionLang, "ar", descEn);

  return {
    id: product._id,
    name: menuNameEn || rawNameEn,
    nameAr: menuNameAr || rawNameAr,
    menuName: menuNameEn || rawNameEn,
    menuNameAr: menuNameAr || rawNameAr,
    description: descEn,
    descriptionAr: descAr,
    price: Number(product.price),
    image: product.imageUrl || "https://placehold.co/400x300/ccc/999?text=No+Image",
    category: categoryId || product.categoryId,
    tags: (product.tags || []).map(normalizeTagItem),
    popular: product.bestSeller,
    ignoreSearch: product.ignoreSearch ?? false,
    blendId: product.blendId,
    foodicsId: product.foodicsId,
    projectId: product.projectId,
    chatBotId: product.chatBotId,
    itemLink: product.itemLink,
    hasSize: product.hasSize,
    sizes: product.sizes || [],
    hasBeverage: product.hasBeverage,
    minBeverageCount: product.minBeverageCount,
    maxBeverageCount: product.maxBeverageCount,
    beverage: product.beverage || [],
    hasTaste: product.hasTaste,
    taste: product.taste || [],
    hasSide: product.hasSide,
    minSidesCount: product.minSidesCount,
    maxSidesCount: product.maxSidesCount,
    sides: product.sides || [],
    hasAddon: product.hasAddon,
    minAddonsCount: product.minAddonsCount,
    maxAddonsCount: product.maxAddonsCount,
    addons: product.addons || [],
    hasBreadsKind: product.hasBreadsKind,
    breadsKind: product.breadsKind || [],
    hasSaladType: product.hasSaladType,
    saladType: product.saladType || [],
    hasAttachedOffer: product.hasAttachedOffer,
    minAttachedOfferCount: product.minAttachedOfferCount,
    maxAttachedOfferCount: product.maxAttachedOfferCount,
    attachedOffer: product.attachedOffer || [],
    hasRiceKind: product.hasRiceKind,
    riceKind: product.riceKind || [],
    removable: [],
  };
}

/** Convert a WebMenuCategory to the legacy Category shape */
export function webCatToCategory(cat: WebMenuCategory): Category {
  return {
    id: cat.id,
    name: cat.name_en,
    nameAr: cat.nameLocalized,
    icon: cat.icon,
  };
}
